// El dibujo como datos (diseño 4a, §2.1): las piezas devuelven figuras, líneas y textos con su caja ya acomodada; la SPA los pinta
// con JSX (sin dangerouslySetInnerHTML) y el reporte (etapa 7) pide el mismo dibujo como texto SVG con aSvg.
// Funciones puras, sin dependencias, sin DOM y sin Date.now().
import type { Fuente } from '../hallazgos/tipos.ts';

/** Fichas de color: no son colores. La SPA las traduce a variables de su hoja de estilos y aSvg a los colores de la guía. */
export type Tinta = 'tinta' | 'tenue' | 'rojo' | 'rojoFondo' | 'cliente' | 'clienteFondo' | 'gris' | 'grisFondo' | 'blanco';

export interface Caja {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * `id` jerárquico con «/»: un texto puede estar dentro de su `duenio` y de las figuras que lo contienen («n2» contiene «n2/sello»).
 * `fuente`: índice en Dibujo.fuentes; la SPA lo vuelve un botón «Ver la fuente».
 * `sancionado`: la referencia (de `sancionados`) de la persona o empresa de la lista a la que pertenece el elemento. Dos sancionados
 * pueden compartir la misma fuente (la lista de OFAC, con la misma fecha): el índice no dice de quién es, esto sí (revisión del PR 3, I1).
 */
export type Elemento =
  | { tipo: 'rect'; id: string; caja: Caja; radio: number; relleno: Tinta; borde: Tinta; discontinuo?: boolean; fuente?: number; sancionado?: string }
  | { tipo: 'circulo'; id: string; cx: number; cy: number; r: number; relleno: Tinta; borde: Tinta }
  | {
    tipo: 'linea';
    id: string;
    puntos: [number, number][];
    borde: Tinta;
    grosor: number;
    discontinua: boolean;
    /** «fin»: punta en el último punto. Sin punta con sentido mutuo o desconocido. */
    flecha: 'fin' | 'ninguna';
    fuente?: number;
    sancionado?: string;
  }
  | { tipo: 'rayado'; id: string; caja: Caja; hueco: string /* q1… */ }
  | {
    tipo: 'texto';
    id: string;
    /** Línea base del texto. */
    x: number;
    y: number;
    ancla: 'inicio' | 'medio' | 'fin';
    tam: number;
    peso: 400 | 500 | 600 | 700;
    tinta: Tinta;
    /** Lo que se pinta (puede ser una línea de un texto partido, o acortado con «…»). */
    contenido: string;
    /** El texto entero, para el título emergente y para la prueba de origen (P9). */
    completo: string;
    /** «documento»: aparece tal cual en el documento; «rotulo»: sale del catálogo ROTULOS (rotulos.ts). */
    origen: 'documento' | 'rotulo';
    /** La caja que la pieza reservó (medida.ts: factor 1.10 y 2 px por lado). */
    caja: Caja;
    duenio?: string;
    fuente?: number;
    sancionado?: string;
  };

export interface Dibujo {
  ancho: number;
  alto: number;
  /** Para lectores de pantalla. */
  descripcion: string;
  elementos: Elemento[];
  fuentes: Fuente[];
  /** Relaciones (hallazgos) que no cupieron en el dibujo y sí están en la lista (el «+n»). */
  omitidos: number;
}

/** Colores de la guía de diseño (docs/producto/prototipos/superficies-riesgo.html). */
export const COLORES: Record<Tinta, string> = {
  tinta: '#1F2624',
  tenue: '#67635A',
  rojo: '#A8342A',
  rojoFondo: '#F7E3DE',
  cliente: '#004F59',
  clienteFondo: '#E4EDEB',
  gris: '#6E695D',
  grisFondo: '#EFEBE2',
  blanco: '#FFFFFF',
};

/** Escapa un texto para SVG: &, <, > y comillas. */
export function escapar(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const n = (v: number) => String(Math.round(v * 100) / 100);
const ANCLA = { inicio: 'start', medio: 'middle', fin: 'end' } as const;

/**
 * El dibujo como texto SVG (lo usará el reporte). Cada <text> lleva sus atributos fijados (familia, peso, interletrado 0, sin
 * transformación de mayúsculas): lo que se mide es lo que se pinta (diseño §2.1, B1).
 */
export function aSvg(d: Dibujo): string {
  const c = (t: Tinta) => COLORES[t];
  const partes: string[] = [];
  const marcas = new Set<Tinta>();
  for (const e of d.elementos) if (e.tipo === 'linea' && e.flecha === 'fin') marcas.add(e.borde);
  partes.push(
    `<svg xmlns="http://www.w3.org/2000/svg" class="argus-pieza" viewBox="0 0 ${n(d.ancho)} ${n(d.alto)}" width="${n(d.ancho)}" height="${
      n(d.alto)
    }" role="img" aria-label="${escapar(d.descripcion)}" style="letter-spacing:0;text-transform:none">`,
  );
  partes.push('<defs>');
  for (const t of marcas) {
    partes.push(
      `<marker id="punta-${t}" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill="${
        c(t)
      }"/></marker>`,
    );
  }
  partes.push(
    `<pattern id="rayado" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="8" stroke="#D6D1C3" stroke-width="3"/></pattern>`,
  );
  partes.push('</defs>');
  for (const e of d.elementos) {
    switch (e.tipo) {
      case 'rect':
        partes.push(
          `<rect id="${escapar(e.id)}" x="${n(e.caja.x)}" y="${n(e.caja.y)}" width="${n(e.caja.w)}" height="${n(e.caja.h)}" rx="${n(e.radio)}" fill="${
            c(e.relleno)
          }" stroke="${c(e.borde)}" stroke-width="1.5"${e.discontinuo ? ' stroke-dasharray="4 3"' : ''}/>`,
        );
        break;
      case 'circulo':
        partes.push(`<circle id="${escapar(e.id)}" cx="${n(e.cx)}" cy="${n(e.cy)}" r="${n(e.r)}" fill="${c(e.relleno)}" stroke="${c(e.borde)}" stroke-width="1.5"/>`);
        break;
      case 'linea':
        partes.push(
          `<polyline id="${escapar(e.id)}" points="${e.puntos.map(([x, y]) => `${n(x)},${n(y)}`).join(' ')}" fill="none" stroke="${c(e.borde)}" stroke-width="${
            n(e.grosor)
          }"${e.discontinua ? ' stroke-dasharray="5 4"' : ''}${e.flecha === 'fin' ? ` marker-end="url(#punta-${e.borde})"` : ''}/>`,
        );
        break;
      case 'rayado':
        partes.push(
          `<rect id="${escapar(e.id)}" data-hueco="${escapar(e.hueco)}" x="${n(e.caja.x)}" y="${n(e.caja.y)}" width="${n(e.caja.w)}" height="${
            n(e.caja.h)
          }" fill="url(#rayado)"/>`,
        );
        break;
      case 'texto':
        partes.push(
          `<text id="${escapar(e.id)}" x="${n(e.x)}" y="${n(e.y)}" text-anchor="${ANCLA[e.ancla]}" font-family="Instrument Sans" font-size="${
            n(e.tam)
          }" font-weight="${e.peso}" letter-spacing="0" style="text-transform:none" fill="${c(e.tinta)}">${
            e.completo !== e.contenido ? `<title>${escapar(e.completo)}</title>` : ''
          }${escapar(e.contenido)}</text>`,
        );
        break;
    }
  }
  partes.push('</svg>');
  return partes.join('');
}
